'use client';

import { type KeyboardEvent, type MouseEvent, memo, useCallback, useEffect, useRef, useState } from 'react';

import { ExpandIcon, ImageIcon, ScanSquareIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

type ZoomLevel = 'fit' | 'full';

const PAN_THRESHOLD_PX = 3;

export type ImagePreviewProps = {
  /** Alt text for the image */
  alt?: string;
  /** Image URL to display */
  src: string | undefined;
};

export const ImagePreview = memo(({ alt = 'Image preview', src }: ImagePreviewProps) => {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('fit');
  const [imageError, setImageError] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  // Reset error state when src changes
  useEffect(() => {
    if (src) {
      setImageError(false);
    }
  }, [src]);

  const containerRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef({ scrollLeft: 0, scrollTop: 0, x: 0, y: 0 });
  const didPanRef = useRef(false);

  const isFullMode = zoomLevel === 'full';

  const showPlaceholder = !src || imageError;

  const handleZoomToggle = useCallback(() => {
    setZoomLevel(zoomLevel === 'fit' ? 'full' : 'fit');
  }, [zoomLevel]);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isFullMode || !containerRef.current) {
        return;
      }
      setIsPanning(true);
      didPanRef.current = false;
      panStartRef.current = {
        scrollLeft: containerRef.current.scrollLeft,
        scrollTop: containerRef.current.scrollTop,
        x: e.clientX,
        y: e.clientY,
      };
    },
    [isFullMode],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isPanning || !containerRef.current) {
        return;
      }
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;

      if (Math.abs(dx) > PAN_THRESHOLD_PX || Math.abs(dy) > PAN_THRESHOLD_PX) {
        didPanRef.current = true;
      }
      containerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
      containerRef.current.scrollTop = panStartRef.current.scrollTop - dy;
    },
    [isPanning],
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleClick = useCallback(() => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written in the pan handlers above; Biome only sees the initial `false`.
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    handleZoomToggle();
  }, [handleZoomToggle]);

  const handleFitClick = useCallback(() => {
    setZoomLevel('fit');
  }, []);

  const handleFullClick = useCallback(() => {
    setZoomLevel('full');
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!showPlaceholder && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        handleZoomToggle();
      }
    },
    [showPlaceholder, handleZoomToggle],
  );

  return (
    <div className="relative flex size-full min-h-0 min-w-0 flex-col overflow-hidden">
      {!showPlaceholder && (
        <div className="absolute bottom-6 left-6 z-10 flex gap-0.5 rounded-8 bg-neutral-4 p-1 text-neutral-12 dark:bg-neutral-6">
          <button
            aria-label="Fit to view"
            aria-pressed={zoomLevel === 'fit'}
            className={cn(
              'flex size-6 items-center justify-center rounded-6 transition-colors',
              zoomLevel === 'fit' && 'bg-white dark:bg-neutral-3',
            )}
            onClick={handleFitClick}
            type="button"
          >
            <ScanSquareIcon size="sm" />
          </button>
          <button
            aria-label="View full resolution"
            aria-pressed={zoomLevel === 'full'}
            className={cn(
              'flex size-6 items-center justify-center rounded-6 transition-colors',
              zoomLevel === 'full' && 'bg-white dark:bg-neutral-3',
            )}
            onClick={handleFullClick}
            type="button"
          >
            <ExpandIcon size="sm" />
          </button>
        </div>
      )}

      {/* biome-ignore lint/a11y/noStaticElementInteractions: Interactive only when showing zoomable image */}
      <div
        className={cn('flex min-h-0 min-w-0 flex-1 overflow-auto', {
          'cursor-grab': !showPlaceholder && isFullMode && !isPanning,
          'cursor-grabbing': !showPlaceholder && isFullMode && isPanning,
          'cursor-zoom-in': !showPlaceholder && !isFullMode,
          'items-center justify-center': !isFullMode || showPlaceholder,
        })}
        onClick={!showPlaceholder ? handleClick : undefined}
        onKeyDown={!showPlaceholder ? handleKeyDown : undefined}
        onMouseDown={isFullMode ? handleMouseDown : undefined}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        ref={containerRef}
        role={!showPlaceholder ? 'button' : undefined}
        tabIndex={!showPlaceholder ? 0 : undefined}
      >
        {showPlaceholder ? (
          <div className="flex flex-col items-center justify-center gap-2 text-neutral-8">
            <ImageIcon size={48} />
            <span className="text-14">No preview available</span>
          </div>
        ) : (
          // biome-ignore lint/performance/noImgElement: External CDN image
          <img
            alt={alt}
            className={isFullMode ? 'm-auto max-h-none max-w-none select-none' : 'size-full object-contain'}
            draggable={false}
            onError={handleImageError}
            src={src}
          />
        )}
      </div>
    </div>
  );
});

ImagePreview.displayName = 'ImagePreview';
